import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import CheckInForm from './pages/CheckInForm';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import Reservations from './pages/Reservations';
import Activities from './pages/Activities';
import ActivityManagement from './pages/ActivityManagement';
import AccessManagement from './pages/AccessManagement';
import Transactions from './pages/Transactions';
import UpcomingReservations from './pages/UpcomingReservations';
import Establishments from './pages/Establishments';
import MonthlyHistory from './pages/MonthlyHistory';
import AccommodationTypes from './pages/AccommodationTypes';
import GlobalDashboard from './pages/GlobalDashboard';
import AffiliateLogin from './pages/AffiliateLogin';
import AffiliateDashboard from './pages/AffiliateDashboard';
import PublicBooking from './pages/PublicBooking';
import AffiliateManagement from './pages/AffiliateManagement';

import ActivitiesLanding from './pages/ActivitiesLanding';

// Use environment variable or default to /api for Vercel
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Detect if we're on the affiliate domain
const isAffiliateDomain = () => {
  const host = window.location.hostname;
  return host.includes('activities.sefertunisia') || host.includes('activities.sefer');
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(res => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate page based on role
    const redirectMap = {
      'admin': '/dashboard',
      'reservation_manager': '/establishment-stats',
      'worker': '/establishment-stats',
      'volunteer': '/upcoming'
    };
    return <Navigate to={redirectMap[user.role] || '/upcoming'} replace />;
  }

  return children;
};

function App() {
  const affiliateMode = isAffiliateDomain();

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {affiliateMode ? (
            <>
              {/* Affiliate domain routes */}
              <Route path="/" element={<ActivitiesLanding />} />
              <Route path="/login" element={<AffiliateLogin />} />
              <Route path="/dashboard" element={<AffiliateDashboard section="dashboard" />} />
              <Route path="/activities" element={<AffiliateDashboard section="activities" />} />
              <Route path="/bookings" element={<AffiliateDashboard section="bookings" />} />
              <Route path="/new-booking" element={<AffiliateDashboard section="new-booking" />} />
              <Route path="/book/:slug" element={<PublicBooking />} />
              <Route path="/:slug" element={<PublicBooking />} />
            </>
          ) : (
            <>
              {/* Main app routes */}
              <Route path="/" element={<CheckInForm />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['admin']}><GlobalDashboard /></ProtectedRoute>} />
              <Route path="/establishment-stats" element={<ProtectedRoute allowedRoles={['admin', 'reservation_manager', 'worker']}><Dashboard /></ProtectedRoute>} />
              <Route path="/reservations" element={<ProtectedRoute><Reservations /></ProtectedRoute>} />
              <Route path="/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
              <Route path="/activity-management" element={<ProtectedRoute allowedRoles={['admin', 'reservation_manager']}><ActivityManagement /></ProtectedRoute>} />
              <Route path="/access-management" element={<ProtectedRoute allowedRoles={['admin']}><AccessManagement /></ProtectedRoute>} />
              <Route path="/transactions" element={<ProtectedRoute allowedRoles={['admin', 'reservation_manager', 'worker']}><Transactions /></ProtectedRoute>} />
              <Route path="/upcoming" element={<ProtectedRoute><UpcomingReservations /></ProtectedRoute>} />
              <Route path="/establishments" element={<ProtectedRoute allowedRoles={['admin']}><Establishments /></ProtectedRoute>} />
              <Route path="/accommodation-types" element={<ProtectedRoute allowedRoles={['admin', 'reservation_manager']}><AccommodationTypes /></ProtectedRoute>} />
              <Route path="/history" element={<ProtectedRoute allowedRoles={['admin', 'reservation_manager']}><MonthlyHistory /></ProtectedRoute>} />
              <Route path="/affiliate-management" element={<ProtectedRoute allowedRoles={['admin']}><AffiliateManagement /></ProtectedRoute>} />
              {/* Affiliate Portal (accessible from main domain too) */}
              <Route path="/affiliate/login" element={<AffiliateLogin />} />
              <Route path="/affiliate/dashboard" element={<AffiliateDashboard section="dashboard" />} />
              <Route path="/affiliate/activities" element={<AffiliateDashboard section="activities" />} />
              <Route path="/affiliate/bookings" element={<AffiliateDashboard section="bookings" />} />
              <Route path="/affiliate/new-booking" element={<AffiliateDashboard section="new-booking" />} />
              {/* Public Booking Page */}
              <Route path="/book/:slug" element={<PublicBooking />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
